import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';

const fetchWsAuthToken = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}ws-auth-token/`, { withCredentials: true });
    return res.data?.token || null;
  } catch {
    return null;
  }
};

/**
 * Get WebSocket URL for tracking summary based on environment
 * @param {string} teamspaceId - The teamspace ID to connect to
 */
const getWebSocketURL = (teamspaceId, authToken = null) => {
  // Always use the current window location to ensure same-origin (cookies work)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  
  // Use window.location.host which includes port if not default (80/443)
  let host = window.location.host;
  
  // For development, explicitly use port 8000 for backend
  // In production, this will use the same host as the frontend
  if (process.env.NODE_ENV === 'development') {
    host = `${window.location.hostname}:8000`;
  }
  
  // Use the same path structure as the API calls
  const BASE_URL = process.env.REACT_APP_BASE_URL || '/';
  let path = '';
  
  if (BASE_URL.startsWith('http://') || BASE_URL.startsWith('https://')) {
    // If BASE_URL is absolute, extract the path
    try {
      const url = new URL(BASE_URL);
      path = url.pathname.replace(/\/$/, '');
    } catch (e) {
      path = '';
    }
  } else {
    // Relative URL
    path = BASE_URL === '/' ? '' : BASE_URL.replace(/\/$/, '');
  }
  
  let wsUrl = `${protocol}//${host}${path}/ws/tracking-summary/${teamspaceId}/`;
  if (authToken) wsUrl += `?auth_token=${authToken}`;
  return wsUrl;
};


// Server message type -> the `kind` passed to onUpdate. Unknown types and
// connection_established / pong are ignored.
const EVENT_KINDS = {
  time_entry_created: 'created',
  time_entry_updated: 'updated',
  time_entry_deleted: 'deleted',
  bulk_update: 'bulk',
  email_draft_created: 'email_draft_created',
  invoicing_changed: 'invoicing_changed',
  clients_projects_changed: 'clients_projects_changed',
};

// Close codes after which retrying is pointless: our own clean close, and the
// consumer's "not authenticated" (4001) / "no access to this teamspace" (4003).
const NO_RETRY_CODES = new Set([1000, 4001, 4003]);
const PING_MS = 30000;
const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 8; // 3s, 6s, 12s, 24s, then 30s -- about 3 minutes in all

/**
 * WebSocket to the tracking-summary consumer (time-entry, email-draft,
 * invoicing and clients/projects change events for every teamspace the user
 * belongs to). One socket per hook call; `onUpdate(kind, data)` is called for
 * each event.
 *
 * Lifecycle rules (2026-09-22 rewrite -- the old version leaked sockets):
 *   - Our own closes use code 1000 and are never retried. A bare close()
 *     reports 1005, which the old code treated as a drop: every unmount /
 *     teamspace switch scheduled a reconnect after cleanup had already run,
 *     leaving a socket nobody would ever close.
 *   - Reconnects on identity changes only (email, teamspace), not when the
 *     `user` object is replaced -- AuthContext replaces it on every tab focus
 *     and every 15-minute revalidation, which used to rebuild every page's
 *     socket (and, with the bug above, leak one) each time.
 *   - A connect superseded while fetching its auth token never opens a socket
 *     (StrictMode's double effect, a quick teamspace switch).
 *   - Only the current socket may schedule a reconnect; a replaced socket's
 *     close event is ignored. No retry on 4001/4003.
 *   - Unexpected drops retry with backoff; if that gives up, the socket is
 *     revived the next time the tab becomes visible.
 *   - `onUpdate` is read through a ref, so a new callback never reconnects.
 *
 * @param {string} teamspaceId - teamspace to connect with (access check only;
 *   the consumer subscribes to all of the user's teamspaces)
 * @param {function} onUpdate - (kind, data) => void
 * @returns {object} - { isConnected, reconnect }
 */
export const useTrackingSummaryWebSocket = (teamspaceId, onUpdate) => {
  const { user, isAuthenticated } = useAuth();
  const userKey = user?.email || null;
  const enabled = Boolean(userKey && isAuthenticated && teamspaceId);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  // Bumped by every connect and every cleanup: an async connect or a close
  // event from an older generation must not touch the current socket.
  const generationRef = useRef(0);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const clearReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const closeCurrent = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
      ws.close(1000);
    }
  }, []);

  const connect = useCallback(async () => {
    // userKey is part of the identity: another logged-in person gets a new socket.
    if (!enabled || !userKey) return;
    const generation = ++generationRef.current;
    clearReconnect();
    closeCurrent();

    let ws;
    try {
      const authToken = await fetchWsAuthToken();
      if (generation !== generationRef.current) return; // superseded while awaiting
      ws = new WebSocket(getWebSocketURL(teamspaceId, authToken));
    } catch (error) {
      return; // connection failed to start; the visibility check will retry later
    }

    let pingInterval = null; // per socket, so an old socket can't stop a new one's ping

    ws.onopen = () => {
      reconnectAttempts.current = 0;
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
      }, PING_MS);
    };

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (error) {
        return; // ignore invalid messages
      }
      const kind = EVENT_KINDS[data?.type];
      if (kind) onUpdateRef.current?.(kind, data);
    };

    ws.onerror = () => {};

    ws.onclose = (event) => {
      clearInterval(pingInterval);
      // Only the current socket reconnects; a replaced or cleaned-up one doesn't.
      if (generation !== generationRef.current || wsRef.current !== ws) return;
      wsRef.current = null;
      if (NO_RETRY_CODES.has(event.code) || reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempts.current, RECONNECT_MAX_MS);
      reconnectAttempts.current += 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        if (generation === generationRef.current) connect();
      }, delay);
    };

    wsRef.current = ws;
  }, [enabled, userKey, teamspaceId, clearReconnect, closeCurrent]);

  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0; // manual reconnect starts a fresh budget
    connect();
  }, [connect]);

  useEffect(() => {
    if (enabled) {
      reconnectAttempts.current = 0;
      connect();
    }
    return () => {
      generationRef.current += 1; // invalidates in-flight connects and pending reconnects
      clearReconnect();
      closeCurrent();
    };
  }, [enabled, connect, clearReconnect, closeCurrent]);

  // A socket that dropped while the tab was hidden (or ran out of retries) is
  // revived as soon as the user comes back to the tab.
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const ws = wsRef.current;
      const alive = ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING);
      if (!alive && !reconnectTimeoutRef.current) {
        reconnectAttempts.current = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, connect]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    reconnect,
  };
};
